import React, { useEffect, useState } from 'react';
import {
    X,
    Clock,
    Share2,
    Sparkles,
    CheckCircle2,
    Lightbulb,
    TrendingUp,
    ShieldCheck,
    Wallet,
    ArrowUpRight,
    Target,
    Info,
} from 'lucide-react';
import { BlogPost, AIInsight } from '../types';
import { generateBlogInsights } from '../services/blogInsights';

interface BlogDetailProps {
    blog: BlogPost;
    onClose: () => void;
}

const BlogDetail: React.FC<BlogDetailProps> = ({ blog, onClose }) => {
    const [insight, setInsight] = useState<AIInsight | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchInsights = async () => {
            setLoading(true);
            const data = await generateBlogInsights(blog.content, blog.title);
            setInsight(data);
            setLoading(false);
        };
        fetchInsights();
    }, [blog]);

    const renderEnhancedContent = () => {
        if (blog.slug !== 'uk-fire-101') {
            return blog.content.split('\n\n').map((para, index) => (
                <p key={index} className="mb-6">
                    {para}
                </p>
            ));
        }

        return (
            <div className="space-y-12">
                <section>
                    <div className="bg-emerald-50 p-8 rounded-3xl border border-emerald-100 mb-8">
                        <h2 className="text-2xl font-black text-emerald-900 mb-4 flex items-center gap-2">
                            <Target className="text-emerald-600" /> 1. The 4% Rule Visualized
                        </h2>
                        <p className="text-emerald-800/80 mb-6">
                            Finding your target is about balancing your “Pot” against your “Pull”.
                        </p>
                        <div className="flex flex-col md:flex-row gap-6 items-end h-64 border-b border-emerald-200 pb-4">
                            <div className="flex-1 flex flex-col items-center">
                                <div className="w-full bg-emerald-600 rounded-t-xl" style={{ height: '100%' }}></div>
                                <span className="text-[10px] font-bold mt-2 uppercase">£750k Pot</span>
                            </div>
                            <div className="flex-1 flex flex-col items-center">
                                <div className="w-full bg-emerald-200 rounded-t-xl" style={{ height: '4%' }}></div>
                                <span className="text-[10px] font-bold mt-2 uppercase">4% Drawdown</span>
                            </div>
                            <div className="flex-1 flex flex-col items-center">
                                <div className="w-full bg-orange-400 rounded-t-xl" style={{ height: '3.5%' }}></div>
                                <span className="text-[10px] font-bold mt-2 uppercase">3.5% Conservative</span>
                            </div>
                        </div>
                    </div>
                </section>

                <section>
                    <h2 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-2">
                        <TrendingUp className="text-emerald-600" /> 2. The Inflation Curve
                    </h2>
                    <div className="relative p-6 bg-white border border-gray-100 rounded-3xl shadow-sm">
                        <svg viewBox="0 0 400 200" className="w-full h-48">
                            <path d="M0,50 Q100,60 200,100 T400,180" fill="none" stroke="#10b981" strokeWidth="4" />
                            <circle cx="0" cy="50" r="5" fill="#10b981" />
                            <circle cx="400" cy="180" r="5" fill="#ef4444" />
                            <text x="10" y="40" fontSize="12" className="fill-gray-400">
                                £30k Today
                            </text>
                            <text x="300" y="170" fontSize="12" className="fill-red-500 font-bold">
                                £62k in 25 Years
                            </text>
                        </svg>
                        <p className="mt-4 text-sm text-gray-500 italic text-center">
                            Visualizing the 3% annual compound effect on purchasing power.
                        </p>
                    </div>
                </section>

                <section>
                    <h2 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-2">
                        3. The Tax Wrapper Stack
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-emerald-600 text-white p-6 rounded-2xl shadow-xl transform hover:-translate-y-1 transition-all">
                            <ShieldCheck className="mb-4 opacity-50" />
                            <h3 className="font-bold text-lg mb-1">Pension</h3>
                            <p className="text-xs opacity-80">Highest tax relief. Locked until 57.</p>
                        </div>
                        <div className="bg-emerald-500 text-white p-6 rounded-2xl shadow-lg transform hover:-translate-y-1 transition-all">
                            <Wallet className="mb-4 opacity-50" />
                            <h3 className="font-bold text-lg mb-1">ISA</h3>
                            <p className="text-xs opacity-80">Tax-free access anytime. Bridge fuel.</p>
                        </div>
                        <div className="bg-emerald-400 text-white p-6 rounded-2xl shadow-md transform hover:-translate-y-1 transition-all">
                            <ArrowUpRight className="mb-4 opacity-50" />
                            <h3 className="font-bold text-lg mb-1">GIA</h3>
                            <p className="text-xs opacity-80">Basic account. Subject to CGT/dividends.</p>
                        </div>
                    </div>
                </section>

                <section className="bg-gray-900 text-white p-10 rounded-[3rem]">
                    <h2 className="text-2xl font-black mb-8 flex items-center gap-2">
                        <Sparkles className="text-emerald-400" /> 10-Step UK FIRE Checklist
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-12">
                        {[
                            'Set Retirement Age & Expectancy',
                            'Estimate Spending (Now + Future)',
                            'Calculate Your FIRE Number',
                            'List All Assets and Liabilities',
                            'Maximize Tax Wrappers (ISA/SIPP)',
                            'Map Income Streams by Age',
                            'Review Withdrawal Coverage %',
                            'Stress-Test for 5% Inflation',
                            'Optimize for Dividend Tax',
                            'Review Progress Annually',
                        ].map((step, idx) => (
                            <div key={idx} className="flex items-center gap-4 group cursor-pointer">
                                <div className="w-6 h-6 rounded-full border border-emerald-500/30 flex items-center justify-center text-[10px] font-bold group-hover:bg-emerald-500 transition-all">
                                    {idx + 1}
                                </div>
                                <span className="text-gray-300 group-hover:text-white transition-colors">{step}</span>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 sm:px-0 bg-black/70 backdrop-blur-md overflow-hidden animate-in fade-in duration-300">
            <div className="bg-white w-full max-w-5xl max-h-[92vh] rounded-[2.5rem] overflow-hidden shadow-2xl flex flex-col relative animate-in zoom-in-95 duration-300 border border-white/20">
                <div className="absolute top-8 right-8 z-20">
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-3 bg-white/10 backdrop-blur-md hover:bg-white/20 rounded-full transition-all text-white border border-white/20 shadow-xl"
                    >
                        <X size={24} />
                    </button>
                </div>

                <div className="overflow-y-auto flex-grow scroll-smooth no-scrollbar">
                    <div className="h-[450px] w-full relative">
                        <img src={blog.imageUrl} alt={blog.title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/20 to-transparent"></div>
                        <div className="absolute bottom-12 left-12 right-12 text-white">
                            <div className="flex items-center gap-3 mb-6">
                                <span className="bg-emerald-600 text-[10px] font-black uppercase tracking-[0.2em] px-4 py-1.5 rounded-full shadow-lg">
                                    {blog.category}
                                </span>
                                <span className="text-emerald-400 text-xs font-bold flex items-center gap-1">
                                    <Clock size={14} /> {blog.readTime}
                                </span>
                            </div>
                            <h1 className="text-4xl md:text-6xl font-black mb-6 leading-[1.1] max-w-3xl">
                                {blog.title}
                            </h1>
                            <div className="flex flex-wrap items-center gap-8 text-sm font-medium opacity-80 border-t border-white/10 pt-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-xs font-bold text-white uppercase">
                                        {blog.author[0]}
                                    </div>
                                    <span>By {blog.author}</span>
                                </div>
                                <span>Published on {blog.date}</span>
                                <div className="flex items-center gap-1 text-emerald-400">
                                    <ShieldCheck size={16} /> Verified Strategy
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="px-12 py-16 flex flex-col lg:flex-row gap-16">
                        <div className="lg:w-[65%]">
                            <div className="prose prose-xl prose-emerald max-w-none text-gray-800 leading-relaxed">
                                <p className="text-2xl font-medium text-gray-500 mb-12 leading-relaxed border-l-8 border-emerald-500 pl-10 py-2">
                                    {blog.excerpt}
                                </p>

                                {renderEnhancedContent()}
                            </div>

                            <div className="mt-20 pt-12 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-6">
                                <div className="flex items-center gap-6">
                                    <div className="w-16 h-16 rounded-3xl bg-emerald-600 flex items-center justify-center text-white text-2xl font-black shadow-lg shadow-emerald-200">
                                        {blog.author[0]}
                                    </div>
                                    <div>
                                        <p className="font-black text-xl text-gray-900">{blog.author}</p>
                                        <p className="text-gray-500 font-medium">Head of Education at Firerify</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <button
                                        type="button"
                                        className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-gray-900 text-white font-bold hover:bg-black transition-all shadow-lg active:scale-95"
                                    >
                                        <Share2 size={18} /> Share Article
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="lg:w-[35%]">
                            <div className="sticky top-8 space-y-8">
                                <div className="bg-emerald-600 rounded-[2.5rem] p-10 text-white shadow-2xl shadow-emerald-200 relative overflow-hidden">
                                    <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>

                                    <div className="flex items-center gap-3 mb-8">
                                        <div className="p-2 bg-white/20 rounded-xl">
                                            <Sparkles size={24} className="text-emerald-200" />
                                        </div>
                                        <h3 className="font-black text-xl uppercase tracking-tight">AI Wealth Assistant</h3>
                                    </div>

                                    {loading ? (
                                        <div className="space-y-6 animate-pulse">
                                            <div className="h-4 bg-white/20 rounded w-3/4"></div>
                                            <div className="h-32 bg-white/20 rounded-3xl"></div>
                                            <div className="h-4 bg-white/20 rounded w-1/2"></div>
                                        </div>
                                    ) : (
                                        insight && (
                                            <div className="space-y-10">
                                                <div>
                                                    <h4 className="text-xs font-black text-emerald-200 uppercase tracking-widest mb-4">
                                                        The Verdict
                                                    </h4>
                                                    <p className="text-lg font-medium text-white leading-relaxed">
                                                        {insight.summary}
                                                    </p>
                                                </div>

                                                <div className="space-y-4">
                                                    <h4 className="text-xs font-black text-emerald-200 uppercase tracking-widest mb-4">
                                                        Action Plan
                                                    </h4>
                                                    {insight.actionItems.map((item, idx) => (
                                                        <div
                                                            key={idx}
                                                            className="flex items-start gap-4 p-4 bg-white/10 rounded-2xl border border-white/10"
                                                        >
                                                            <CheckCircle2 size={20} className="text-emerald-300 shrink-0" />
                                                            <span className="text-sm font-bold text-white">{item}</span>
                                                        </div>
                                                    ))}
                                                </div>

                                                <div className="bg-emerald-900/40 p-6 rounded-3xl border border-white/10">
                                                    <div className="flex items-center gap-3 mb-3 text-emerald-300 font-black text-xs uppercase tracking-widest">
                                                        <Lightbulb size={16} /> Expert Pro Tip
                                                    </div>
                                                    <p className="text-sm font-bold text-white italic leading-relaxed">
                                                        "{insight.wealthTip}"
                                                    </p>
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>

                                <div className="bg-gray-50 border border-gray-100 p-8 rounded-[2.5rem]">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Info size={18} className="text-gray-400" />
                                        <h4 className="font-black text-xs uppercase text-gray-400 tracking-widest">
                                            About this Guide
                                        </h4>
                                    </div>
                                    <p className="text-xs text-gray-500 leading-relaxed font-medium">
                                        This guide follows the 2024/25 UK Tax Rules. Tax legislation is subject to change.
                                        Always consult a certified financial planner for personalized advice.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BlogDetail;
